import {
    getDiff,
    getHistory,
    getHolidayHistory,
    getRecordOverrides,
    getScheduleStatus,
    getVersions,
} from './http/tradingHoursAdmin';
import type { HolidayHistoryParams } from './http/tradingHoursAdmin';
import type { FetchQueryOptions, QueryClient } from '@tanstack/query-core';

// Models may not import `queries/http/**`; the TradingHours admin model writes through these.
export {
    downloadArchive,
    freeze,
    releaseRecordOverride,
    setRecordsBlacklist,
    setVersionsBlacklist,
    unfreeze,
} from './http/tradingHoursAdmin';
export type { HolidayHistoryParams } from './http/tradingHoursAdmin';

/**
 * QueryKey constants
 */
export const TRADING_HOURS_ADMIN = 'trading-hours-admin';

/**
 * Every lever changes what is served, so the whole family is invalidated after any write. The
 * shared QueryClient's `staleTime` is one day; these are operator views and must refetch on
 * every visit, so each query sets its own `staleTime: 0`. The shared client also retries
 * transport failures indefinitely, which would keep the page on "loading" forever when the worker
 * is unreachable (no VPN); one retry is enough here, after which the page explains what to do.
 */
const OPERATOR_QUERY = { staleTime: 0, retry: 1 } as const;
export const getTradingHoursStatusQuery = (): FetchQueryOptions<Awaited<ReturnType<typeof getScheduleStatus>>> => ({
    queryKey: [TRADING_HOURS_ADMIN, 'status'],
    queryFn: () => getScheduleStatus(),
    ...OPERATOR_QUERY,
});

export const getTradingHoursVersionsQuery = (
    limit: number,
): FetchQueryOptions<Awaited<ReturnType<typeof getVersions>>> => ({
    queryKey: [TRADING_HOURS_ADMIN, 'versions', limit],
    queryFn: () => getVersions(limit),
    ...OPERATOR_QUERY,
});

export const getTradingHoursHistoryQuery = (
    ingestionId: string | undefined,
    limit: number,
): FetchQueryOptions<Awaited<ReturnType<typeof getHistory>>> => ({
    queryKey: [TRADING_HOURS_ADMIN, 'history', ingestionId ?? null, limit],
    queryFn: () => getHistory(ingestionId ? { ingestionId, limit } : { limit }),
    ...OPERATOR_QUERY,
});

export const getTradingHoursOverridesQuery = (): FetchQueryOptions<Awaited<ReturnType<typeof getRecordOverrides>>> => ({
    queryKey: [TRADING_HOURS_ADMIN, 'overrides'],
    queryFn: () => getRecordOverrides(),
    ...OPERATOR_QUERY,
});

export const getTradingHoursDiffQuery = (
    from: string,
    to: string,
): FetchQueryOptions<Awaited<ReturnType<typeof getDiff>>> => ({
    queryKey: [TRADING_HOURS_ADMIN, 'diff', from, to],
    queryFn: () => getDiff(from, to),
    ...OPERATOR_QUERY,
});

export const getTradingHoursHolidayHistoryQuery = (
    params: HolidayHistoryParams,
): FetchQueryOptions<Awaited<ReturnType<typeof getHolidayHistory>>> => ({
    queryKey: [TRADING_HOURS_ADMIN, 'holiday-history', params.date, params.mic ?? null, params.finId ?? null],
    queryFn: () => getHolidayHistory(params),
    ...OPERATOR_QUERY,
});

export const invalidateTradingHoursAdminQueries = (queryClient: QueryClient): Promise<void> =>
    queryClient.invalidateQueries({ queryKey: [TRADING_HOURS_ADMIN] });
